// Domain · GdStatusChip
//
// Tone- and label-coded renderer for the `price_lists.gd_status` enum
// (`"yes" | "no" | "na"`). Reuses the centralised `GD_STATUS_COLORS`
// map from `@ds/tokens/domainTones`.
//
// Variants mirror `EntityChip`:
//   - `"chip"` (default) — Pill chrome.
//   - `"inline"`         — inline tone-colored span.

import React from "react";
import { Pill, type PillSize } from "@ds/display/Pill";
import { TextValue } from "@ds/display/TextValue";
import { gdStatusTone, gdStatusText, type GdStatus } from "@ds/domain/GdStatusChip.helpers";

export type { GdStatus } from "@ds/domain/GdStatusChip.helpers";


// ─── Inputs ───────────────────────────────────────────────────────────────

export interface GdStatusChipInput {
  /** GD status enum value. `null` / `undefined` → em-dash. */
  status: GdStatus | null | undefined;
  /** Rendering variant. Default `"chip"`. */
  variant?: "chip" | "inline";
  /** Pill size — only meaningful when `variant="chip"`. Default `"md"`. */
  size?: PillSize;
  /** `data-testid` on the root. Defaults to `"gd-status-chip"`. */
  testId?: string;
}

export type GdStatusChipProps = GdStatusChipInput;

// ─── Component ────────────────────────────────────────────────────────────

export function GdStatusChip({
  status,
  variant = "chip",
  size = "md",
  testId = "gd-status-chip",
}: GdStatusChipProps) {
  const tone = gdStatusTone(status);
  const text = gdStatusText(status);

  if (!status || !text) {
    return <span data-testid={testId}>—</span>;
  }

  if (variant === "inline") {
    return (
      <TextValue tone={tone} testId={testId}>
        {text}
      </TextValue>
    );
  }

  return (
    <Pill tone={tone} size={size} testId={testId}>
      {text}
    </Pill>
  );
}
